// Rejected goods step 3a (2026-10-25; approved mockup AUMPidbNn2tJqJ4ujXtcPv, "Payments & ADIB" 2): the PO page's Payments card —
// every payment with its proof and whether the ADIB statement confirms it, credits and refunds from Returns & credits, and the
// lines' value, paid, came back and what is still to pay (or what the vendor owes us). Hidden for people without access to the
// purchasing account. ("Record the payment now?" after a direct purchase or a receipt: useRecordPaymentPrompt.)
// Ahmed 1 Nov: each payment can be corrected or cancelled from its row; entries that no longer count stay on the card, struck through,
// as replaced or cancelled, with the reason.
// Step 4j (Ahmed 2 Nov): a credit from another case taken off this PO shows as its own row and lowers what is to pay.
// Step 5b (item 8, mockup "PO page: before this system"): a PO dated before the go-live date reads "Before this system", with no table and no
// Record payment; a PO on the opening list shows what was paid before go-live as its first row.
// Step 5d (items 10, 11): a cash payment with no paper shows why; a paid PO with no invoice offers "This PO will never have an invoice", with a reason, and Undo.
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, Modal, Space, Typography, message } from 'antd';
import { DollarOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getPaymentProofLink, getPoPayments, PoPayments, setPoNeverInvoice, undoPoNeverInvoice } from '../../api';
import ResponsiveTable from '../responsive/ResponsiveTable';
import { StatusPill } from '../table-cells';
import type { Tone } from '../table-cells';
import { money } from '../receiving/shared';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import RecordPaymentWindow from './RecordPaymentWindow';
import CancelPaymentWindow from './CancelPaymentWindow';

const { Text } = Typography;
const MUTED = '#8a94a6';
const HOW: Record<string, string> = { credit_note: 'Credit note', refund: 'Refund', other_invoice: 'Taken off another invoice', paid_less: 'Paid less' };

interface Row { key: string; date: string | null; how: string; ref: React.ReactNode; amount: number; egp?: number | null; closed?: boolean; bank: { tone: Tone; label: string; extra?: string } }

const PoPaymentsCard: React.FC<{ poId: string; canRecord: boolean; refreshKey?: number; onChanged?: () => void }> = ({ poId, canRecord, refreshKey, onChanged }) => {
  const [data, setData] = useState<PoPayments | null>(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [correcting, setCorrecting] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [neverWhy, setNeverWhy] = useState<string | null>(null);
  const compact = useCompactLayout();

  const load = useCallback(async () => {
    try { setData(await getPoPayments(poId)); setHidden(false); } catch (e: any) { if (e?.response?.status === 403) setHidden(true); }
  }, [poId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (hidden || !data) return null;
  const t = data.totals;
  const vendor = data.po.vendor?.name || 'The vendor';
  const openProof = async (paymentId: number, i: number) => {
    try { window.open(await getPaymentProofLink(paymentId, i), '_blank', 'noopener'); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not open the file'); }
  };
  const o = data.opening;
  const usd = data.po.currency && data.po.currency !== 'EGP' ? data.po.currency : null;
  const cur = (n: number) => (usd ? `${usd} ${money(n)}` : money(n));
  const rows: Row[] = [
    ...(o ? [{
      key: 'opening', date: o.on, how: o.answer === 'not_paid' ? 'Opening list' : 'Before this system',
      ref: <span style={{ color: MUTED }}>{o.answer === 'not_paid' ? 'Not paid before go-live' : o.answer === 'part' ? 'Paid before go-live, from the opening list' : 'Paid in full before go-live, from the opening list'} · ticked by the officer, approved by the CEO</span>,
      amount: o.amount, bank: { tone: 'mute' as Tone, label: 'none expected' },
    }] : []),
    ...data.payments.map((p) => ({
      key: `p${p.id}`, date: p.paidOn,
      how: `${p.methodLabel}${p.pos.length > 1 ? ` · also ${p.pos.filter((x) => x !== data.po.poId).join(', ')}` : ''}`,
      ref: (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'center' }}>
          <span style={{ color: MUTED }}>{p.code}</span>
          {p.reference && <span>{p.reference}</span>}
          {p.proofFiles.map((f, i) => (
            <Button key={f.key} type="link" size="small" icon={<PaperClipOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => openProof(p.id, i)}>{f.name}</Button>
          ))}
          {p.noPaper && <span>No paper: <bdi>{p.noPaper}</bdi></span>}
          {canRecord && (
            <span style={{ flexBasis: '100%', display: 'inline-flex', gap: 10, flexWrap: 'wrap' }}>
              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => setCorrecting(p.id)}>Correct</Button>
              <Button type="link" size="small" danger style={{ padding: 0, height: 'auto' }} onClick={() => setCancelling(p.id)}>Cancel this payment</Button>
            </span>
          )}
        </span>
      ),
      amount: p.onThisPo ?? p.amount,
      // Step 5e (item 12): on a USD PO the row is in USD, and paid in pounds it also shows the EGP and the rate.
      egp: p.paidCurrency === 'EGP' ? p.amount : null,
      bank: p.bank.state === 'matched'
        ? { tone: 'ok' as Tone, label: `matched · ADIB ${dayjs(p.bank.on).format('D MMM')}`, extra: p.bank.difference ? `+${money(p.bank.difference)} ${p.bank.reason === 'fee' ? 'fee' : 'difference'}` : undefined }
        : p.bank.state === 'waiting' ? { tone: 'wait' as Tone, label: 'waiting for the statement' }
          // Step 4c (M3): a purchase on the vendor's credit says whether finance has paid it, and with which FIN- payment.
          : p.method === 'vendor_credit' && p.credit
            ? (p.credit.outstanding <= 0.005
              ? { tone: 'ok' as Tone, label: `paid by finance${p.credit.by.length ? ` · ${p.credit.by.map((x) => x.code).join(', ')}` : ''}`, extra: p.credit.by[0]?.on ? dayjs(p.credit.by[p.credit.by.length - 1].on).format('D MMM') : undefined }
              : p.credit.covered > 0.005
                ? { tone: 'wait' as Tone, label: `on credit · finance paid ${money(p.credit.covered)}`, extra: `${money(p.credit.outstanding)} still owed · ${p.credit.by.map((x) => x.code).join(', ')}` }
                : { tone: 'wait' as Tone, label: 'owed on the vendor\'s credit', extra: 'finance pays the vendor' })
            : { tone: 'mute' as Tone, label: p.method === 'cash' ? 'no bank line (paid from ATM cash)' : 'no bank line (finance)' },
    })),
    ...data.credits.map((c) => ({
      key: `c${c.key}`, date: c.settlement?.on || null, how: HOW[c.settlement!.how] || c.settlement!.how,
      ref: <span style={{ color: MUTED }}>{[c.settlement?.reference, c.kind === 'invoice' ? `invoice ${c.code || ''}`.trim() : c.kind === 'short_close' ? 'short close' : c.kind === 'overpaid' ? 'money paid over' : c.code || 'discount', c.settlement?.payment].filter(Boolean).join(' · ')}</span>,
      amount: -(c.settlement?.amount || 0),
      bank: c.settlement?.how === 'refund' && c.settlement.refundMethod && c.settlement.refundMethod !== 'cash'
        ? { tone: 'mute' as Tone, label: 'back into ADIB' }
        : c.settlement?.how === 'paid_less' ? { tone: 'mute' as Tone, label: 'taken off the payment' } : { tone: 'mute' as Tone, label: 'no bank line (credit)' },
    })),
    ...(data.takenOff || []).map((o) => ({
      key: `o${o.id}`, date: o.on, how: 'Taken off here',
      ref: <span style={{ color: MUTED }}>{[o.reference, o.what, o.fromPo ? `on ${o.fromPo}` : null].filter(Boolean).join(' · ')}</span>,
      amount: -o.amount, bank: { tone: 'mute' as Tone, label: 'no bank line (credit)' },
    })),
    // Entries that no longer count: nothing above adds them to the totals.
    ...(data.closed || []).map((p) => ({
      key: `x${p.id}`, date: p.paidOn, how: p.methodLabel, closed: true,
      ref: (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'center', color: MUTED }}>
          <span>{p.code}</span>
          {p.reference && <span>{p.reference}</span>}
          {p.proofFiles.map((f, i) => (
            <Button key={f.key} type="link" size="small" icon={<PaperClipOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => openProof(p.id, i)}>{f.name}</Button>
          ))}
          {p.why && <span style={{ flexBasis: '100%', fontSize: 12 }}><bdi>{p.why}</bdi></span>}
        </span>
      ),
      amount: p.onThisPo ?? p.amount,
      bank: {
        tone: 'mute' as Tone, label: p.status === 'cancelled' ? 'cancelled' : `replaced${p.replacedBy ? ` by ${p.replacedBy}` : ''}`,
        extra: [p.by, p.at ? dayjs(p.at).format('D MMM') : null].filter(Boolean).join(' · ') || undefined,
      },
    })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (data.beforeSystem) {
    return (
      <Card title={<Space size={8} wrap><span>Payments</span><StatusPill tone="mute" label="Before this system" /></Space>} style={{ marginBottom: 16 }}>
        <div style={{ border: '1px dashed #cfd4dd', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
          Paid before {data.goLiveOn ? dayjs(data.goLiveOn).format('D MMM YYYY') : 'the go-live date'}, outside the ERP. No payment is recorded, and this PO is not on To pay, Add a PO or Invoices to check.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, marginTop: 10 }}>
          <span>Lines' value <b>{money(t.linesValue)}</b></span>
          <span>Invoiced {t.invoiced == null ? <Text type="secondary">— (no invoice confirmed)</Text> : <b>{money(t.invoiced)}</b>}</span>
          <span>Paid <Text type="secondary">before this system</Text></span>
        </div>
      </Card>
    );
  }
  const pill = data.status === 'paid'
    ? { tone: 'ok' as Tone, label: `Paid in full${data.bankConfirmed ? ' · bank confirmed' : ''}` }
    : data.status === 'part_paid' ? { tone: 'warn' as Tone, label: `Part paid · ${usd || 'EGP'} ${money(t.toPay)} to pay` }
      : data.status === 'overpaid' ? { tone: 'info' as Tone, label: `${vendor} owes us EGP ${money(t.vendorOwes ?? -t.toPay)}` }
        : { tone: 'wait' as Tone, label: `Not paid · ${usd || 'EGP'} ${money(t.toPay)} to pay` };

  return (
    <Card
      title={compact ? 'Payments' : <Space size={8} wrap><span>Payments</span><StatusPill tone={pill.tone} label={pill.label} /></Space>}
      style={{ marginBottom: 16 }}
      extra={canRecord && <Button type="primary" size="small" icon={<DollarOutlined />} onClick={() => setOpen(true)}>Record payment</Button>}
    >
      {compact && <div style={{ marginBottom: 10 }}><StatusPill tone={pill.tone} label={pill.label} /></div>}
      <ResponsiveTable<Row>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'No payment recorded yet' }}
        card={{ header: ['date', 'amount'] }}
        columns={[
          { title: 'Date', key: 'date', width: 80, render: (_: any, r: Row) => (r.date ? dayjs(r.date).format('D MMM') : '-') },
          { title: 'How', key: 'how', width: 190, render: (_: any, r: Row) => (r.closed ? <span style={{ color: MUTED }}>{r.how}</span> : r.how) },
          { title: 'Reference · proof', key: 'ref', render: (_: any, r: Row) => r.ref },
          { title: usd || 'EGP', key: 'amount', width: 120, align: 'right' as const, render: (_: any, r: Row) => <span style={{ fontVariantNumeric: 'tabular-nums', ...(r.closed ? { textDecoration: 'line-through', color: MUTED } : {}) }}>{r.amount < 0 ? `− ${money(-r.amount)}` : money(r.amount)}</span> },
          ...(usd ? [{ title: 'EGP', key: 'egp', width: 140, align: 'right' as const, render: (_: any, r: Row) => (r.egp != null ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.egp)}{r.amount > 0 ? <div style={{ fontSize: 11.5, color: MUTED }}>at {money(r.egp / r.amount)}</div> : null}</span> : <Text type="secondary">—</Text>) }] : []),
          { title: 'Bank', key: 'bank', width: 230, render: (_: any, r: Row) => <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px 6px', alignItems: 'center', justifyContent: compact ? 'flex-end' : undefined }}><StatusPill tone={r.bank.tone} label={r.bank.label} />{r.bank.extra && <span style={{ fontSize: 12, color: MUTED }}>{r.bank.extra}</span>}</span> },
        ]}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, marginTop: 10 }}>
        <span>Lines' value <b>{cur(t.linesValue)}</b></span>
        {/* Step 4d (M4): the confirmed invoices' total, and charges recorded on the PO from them. */}
        <span>Invoiced {t.invoiced == null ? <Text type="secondary">— (no invoice confirmed)</Text> : <b>{money(t.invoiced)}</b>}</span>
        {(t.charges || 0) > 0 && <span>Charges <b>{money(t.charges || 0)}</b></span>}
        <span>Paid <b>{cur(t.paid)}</b></span>
        {t.cameBack > 0 && <span>Came back <b>{money(t.cameBack)}</b></span>}
        {t.discountTotal > 0 && <span>Discounts <b>{money(t.discountTotal)}</b></span>}
        {(t.takenOff || 0) > 0 && <span>Taken off here <b>{money(t.takenOff || 0)}</b></span>}
        {/* Step 4a: per line, never netted: a short close or discount after paying is money the vendor owes us (its case is on Returns &
            credits); it does not shrink what is still to pay on the other lines. */}
        {t.toPay > 0.5 && <span>To pay <b>{cur(t.toPay)}</b></span>}
        {(t.vendorOwes ?? 0) > 0.5 && <span><bdi>{vendor}</bdi> owes us <b>{money(t.vendorOwes)}</b></span>}
      </div>
      {/* Step 5d (item 11): paid with no invoice, it is on Invoices to check until an invoice is added or it is marked as never having one. */}
      {t.invoiced == null && t.paid > 0 && (data.neverInvoice
        ? (
          <div style={{ marginTop: 10, border: '1px solid #b7eb8f', background: '#f6ffed', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
            <b>Never has an invoice:</b> <bdi>{data.neverInvoice.reason}</bdi> · {data.neverInvoice.by === 'vendor' ? 'ticked on the vendor' : `set by ${data.neverInvoice.setBy || 'the officer'}${data.neverInvoice.setAt ? `, ${dayjs(data.neverInvoice.setAt).format('D MMM')}` : ''}`}
            {data.neverInvoice.by === 'po' && canRecord && <> · <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={async () => { try { await undoPoNeverInvoice(data.po.poId); load(); onChanged?.(); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not undo it'); } }}>Undo</Button></>}
          </div>
        ) : (
          <div style={{ marginTop: 10, border: '1px dashed #cfd4dd', borderRadius: 8, padding: '8px 12px', fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
            <span><b>No invoice yet.</b> This PO is on Invoices to check under "Paid, no invoice".</span>
            {canRecord && (
              <Space size={6} wrap>
                <Button size="small" onClick={() => { document.getElementById('invoice-check')?.scrollIntoView({ behavior: 'smooth' }); window.dispatchEvent(new Event('po-add-invoice')); }}>Add the invoice</Button>
                <Button size="small" onClick={() => setNeverWhy('')}>This PO will never have an invoice</Button>
              </Space>
            )}
          </div>
        ))}
      <Modal
        open={neverWhy != null} title="This PO will never have an invoice" okText="Save" cancelText="Go back" okButtonProps={{ disabled: !(neverWhy || '').trim() }}
        onCancel={() => setNeverWhy(null)}
        onOk={async () => { try { await setPoNeverInvoice(data.po.poId, (neverWhy || '').trim()); setNeverWhy(null); load(); onChanged?.(); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not save it'); } }}
      >
        <label htmlFor="po-never-why" style={{ display: 'block', marginBottom: 6 }}>Why? <Text type="secondary">(for example: a plumber paid by the day, no paper at all)</Text></label>
        <Input.TextArea id="po-never-why" rows={3} value={neverWhy || ''} onChange={(e) => setNeverWhy(e.target.value)} maxLength={1000} autoFocus />
        <div style={{ fontSize: 12, color: '#5f6672', marginTop: 6 }}>It leaves Invoices to check, and the reason stays on this card.</div>
      </Modal>
      <RecordPaymentWindow poId={open ? data.po.poId : null} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); onChanged?.(); }} />
      <RecordPaymentWindow poId={correcting ? data.po.poId : null} correcting={correcting} onClose={() => setCorrecting(null)} onSaved={() => { setCorrecting(null); load(); onChanged?.(); }} />
      <CancelPaymentWindow paymentId={cancelling} onClose={() => setCancelling(null)} onSaved={() => { setCancelling(null); load(); onChanged?.(); }} />
    </Card>
  );
};

export default PoPaymentsCard;
